import type {
  AgentPresentation,
  AgentPresentationStep,
} from "./agent-presentation-types";
import { usePresentationGuideStore } from "@/stores/presentation-guide-store";
import {
  subscribePresentationGuideCommands,
  sendPresentationGuideCommand,
} from "./presentation-guide-control";
import {
  resolvePresentationPoint,
  resolvePresentationRect,
} from "./agent-presentation-contract";
import {
  componentComplete,
  reportStepProgress,
} from "./presentation-step-progress";
import { observePresentationGuideAction } from "./presentation-guide-observer";

/** Pausing aborts only this attempt. Resuming replays this step and narration;
 * cancellation/replacement aborts the parent run and releases every listener. */
export async function runGuidedStep(
  presentation: AgentPresentation,
  step: AgentPresentationStep,
  stepIndex: number,
  signal: AbortSignal,
  perform: (
    attempt: AbortSignal,
    onVisible: () => void,
    replayCount: number,
  ) => Promise<boolean>,
  onCancel?: () => void,
): Promise<boolean> {
  const action = "user_action" in step ? step.user_action : undefined;
  const rect =
    step.kind === "highlight" || step.kind === "draw"
      ? resolvePresentationRect(step, presentation)
      : null;
  const point =
    step.kind === "point" ? resolvePresentationPoint(step, presentation) : null;
  const anchor = rect
    ? {
        x: rect.x + rect.width / 2,
        y: rect.y + rect.height / 2,
        displayId: rect.displayId,
      }
    : (point ?? undefined);
  let paused = false;
  let cancelled = false;
  let done = componentComplete(
    presentation.resumeReceipt?.find((item) => item.step_id === step.step_id)
      ?.user_action,
  );
  let replayCount = 0;
  let attempt = new AbortController();
  let wake: (() => void) | undefined;
  const publish = (phase: "presenting" | "waiting" | "paused") => {
    usePresentationGuideStore.setState({
      guide: {
        presentationId: presentation.presentationId,
        stepId: step.step_id,
        stepIndex,
        stepCount: presentation.steps.length,
        action,
        phase,
        replayCount,
        anchor,
      },
    });
  };
  const unsubscribe = subscribePresentationGuideCommands(
    (command) => {
      if (
        command.presentationId !== presentation.presentationId ||
        (["repeat", "continue", "detected"].includes(command.action) &&
          command.stepId !== step.step_id)
      )
        return;
      if (command.action === "cancel") {
        cancelled = true;
        onCancel?.();
        attempt.abort();
      }
      if (command.action === "pause" && !paused) {
        paused = true;
        attempt.abort();
        publish("paused");
      }
      if (command.action === "resume" && paused) {
        paused = false;
        replayCount += 1;
      }
      if (command.action === "repeat" && !paused) {
        replayCount += 1;
        done = false;
        attempt.abort();
      }
      if (
        !paused &&
        (command.action === "detected" || command.action === "continue")
      ) {
        done = true;
        if (action)
          void reportStepProgress(
            presentation,
            step,
            "user_action",
            "finished",
          );
      }
      wake?.();
    },
    { presentationId: presentation.presentationId, stepId: step.step_id },
  );
  const onAbort = () => {
    attempt.abort();
    wake?.();
  };
  signal.addEventListener("abort", onAbort, { once: true });
  try {
    while (!signal.aborted && !cancelled) {
      if (paused) {
        await new Promise<void>((resolve) => {
          wake = resolve;
        });
        wake = undefined;
        continue;
      }
      attempt = new AbortController();
      publish("presenting");
      let stopObserving = () => {};
      try {
        if (action)
          stopObserving = observePresentationGuideAction(
            presentation,
            step,
            () => {
              sendPresentationGuideCommand({
                presentationId: presentation.presentationId,
                stepId: step.step_id,
                action: "detected",
              });
            },
          );
        const completed = await perform(attempt.signal, () => {}, replayCount);
        if (attempt.signal.aborted) continue;
        if (!completed) return false;
        if (!action) return true;
        publish("waiting");
        if (
          !done &&
          !(await reportStepProgress(
            presentation,
            step,
            "user_action",
            "waiting",
          ))
        )
          return false;
        while (
          !done &&
          !paused &&
          !attempt.signal.aborted &&
          !signal.aborted &&
          !cancelled
        ) {
          await new Promise<void>((resolve) => {
            wake = resolve;
          });
          wake = undefined;
        }
        if (
          done &&
          !paused &&
          !attempt.signal.aborted &&
          !signal.aborted &&
          !cancelled
        )
          return reportStepProgress(
            presentation,
            step,
            "user_action",
            "finished",
          );
      } catch (error) {
        if (!attempt.signal.aborted) throw error;
      } finally {
        stopObserving();
      }
    }
    return false;
  } finally {
    unsubscribe();
    signal.removeEventListener("abort", onAbort);
    if (
      usePresentationGuideStore.getState().guide?.presentationId ===
      presentation.presentationId
    ) {
      usePresentationGuideStore.setState({ guide: null });
    }
  }
}
